import React, { useState } from 'react';
import { Star, Clock, ChevronDown } from 'lucide-react';
import { parseLocalDateTime, formatTime } from '../../utils/timeUtils';
import type { RoutyPlaceCard } from '../../business/routy/routyArtifacts';
import PlaceCard from './PlaceCard';

interface PlacesListProps {
  cards: RoutyPlaceCard[];
  durationMinutes: number;
  disabled?: boolean;
  onSchedule: (place: RoutyPlaceCard, durationMinutes: number) => void;
}

function addedDriveMinutes(place: RoutyPlaceCard): number | null {
  const inMin = place.driveIn?.minutes ?? null;
  const outMin = place.driveOut?.minutes ?? null;
  if (inMin == null && outMin == null) return null;
  return (inMin ?? 0) + (outMin ?? 0);
}

/**
 * Renders Routy's place suggestions as a compact accordion: the top
 * (most route-efficient) option is highlighted and expanded so the user can act
 * immediately, while the rest stay condensed until the user opens them. Keeps
 * the chat readable when several options are returned.
 */
const PlacesList: React.FC<PlacesListProps> = ({ cards, durationMinutes, disabled, onSchedule }) => {
  const [expandedId, setExpandedId] = useState<string | null>(cards[0]?.placeId ?? null);

  if (cards.length === 0) return null;

  return (
    <div className="ml-10 space-y-2">
      {cards.map((place, index) => {
        const isTop = index === 0;
        const expanded = expandedId === place.placeId;

        if (expanded) {
          return (
            <PlaceCard
              key={place.placeId}
              place={place}
              durationMinutes={durationMinutes}
              disabled={disabled}
              onSchedule={onSchedule}
              recommended={isTop}
              collapsible={cards.length > 1}
              onCollapse={() => setExpandedId(null)}
            />
          );
        }

        const start = place.date && place.startTime ? parseLocalDateTime(place.date, place.startTime) : null;
        const added = addedDriveMinutes(place);

        return (
          <button
            key={place.placeId}
            onClick={() => setExpandedId(place.placeId)}
            className={`w-full text-left rounded-lg border bg-surface px-3 py-2.5 flex items-center gap-3 transition-colors ${
              isTop ? 'border-blue-300 ring-1 ring-blue-200' : 'border-ink-200 hover:border-ink-300 hover:bg-ink-50'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {isTop && (
                  <span className="text-2xs font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded-md flex-shrink-0">
                    Best match
                  </span>
                )}
                <span className="text-sm font-semibold text-ink-900 truncate">{place.name}</span>
              </div>
              <div className="mt-0.5 flex items-center gap-2.5 text-xs text-ink-500">
                {place.rating != null && (
                  <span className="flex items-center gap-0.5">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                    {place.rating.toFixed(1)}
                  </span>
                )}
                {start && (
                  <span className="flex items-center gap-0.5">
                    <Clock className="w-3 h-3" />
                    {formatTime(start)}
                  </span>
                )}
                {added != null && <span className="text-ink-400">+{added} min drive</span>}
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-ink-400 flex-shrink-0" />
          </button>
        );
      })}
    </div>
  );
};

export default PlacesList;
